import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

import { localDayAfter, localDayStart } from "@/components/shared/DateRangeFilter";
import { UsageService } from "@/services/usageService";
import { useDebounce } from "@/hooks/useDebounce";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

/**
 * Turn a `{from, to}` pair of yyyy-mm-dd strings into the usage endpoints'
 * `from` / `to` parameters.
 *
 * `to` is an exclusive upper bound server-side, so the chosen end date is
 * rolled forward a day. Without that, picking today silently excludes
 * everything that happened today.
 *
 * The days come from the same `DateRangeFilter` the audit tabs use, so the
 * timezone convention comes from there too — a picked day is that day where
 * the reader sits. Reading these strings as UTC days instead put the usage
 * window hours away from the audit window over the same pair of dates.
 */
export const windowParams = ({ from, to } = {}) => {
  const params = {};
  if (from) params.from = localDayStart(from);
  if (to) params.to = localDayAfter(to);
  return params;
};

/**
 * One tenant's usage for a SuperAdmin: the summary and the per-user breakdown,
 * from the single bundled endpoint so the two cannot straddle a write and
 * disagree.
 *
 * Shared by the tenant-details tab and the SuperAdmin usage page, which is the
 * point: both screens ask the same code the same question, so they cannot drift
 * apart the way they did when each owned its own fetch.
 *
 * **Two busy flags, one fetch.** The scope, the window and the engineering
 * switch rebuild the whole panel, so they raise `loading`. A search rebuilds
 * the per-user table only, so it raises `searchLoading` and leaves the other
 * controls usable — with one flag, every keystroke burst greyed out the date
 * picker.
 *
 * Search is server-side. The server drops the unattributed buckets for a search
 * and recomputes the totals and the note beside them, none of which a local
 * `.filter()` could do.
 */
export const useTenantUsage = ({ tenantId, range, detail, search }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const debouncedSearch = useDebounce(search ?? "", 400);

  // Everything except the search. Comparing it against the last run tells the
  // effect which of the two flags to raise.
  const filterKey = useMemo(
    () => JSON.stringify({ tenantId, from: range?.from ?? "", to: range?.to ?? "", detail: !!detail }),
    [tenantId, range?.from, range?.to, detail],
  );
  const lastFilterKey = useRef(null);
  const lastTenantId = useRef(null);

  useEffect(() => {
    if (!tenantId) {
      lastTenantId.current = null;
      setData(null);
      setLoading(false);
      return undefined;
    }

    // A new tenant drops the old one's numbers at once. A new window or a new
    // depth does not: those keep the figures on screen while the fresh ones
    // load, which reads as a refresh rather than a reset. Showing tenant A's
    // tokens under tenant B's name, even for one frame, is the one thing this
    // screen must never do.
    if (lastTenantId.current !== tenantId) {
      lastTenantId.current = tenantId;
      setData(null);
      setLoading(true);
    }

    const isFilterChange = lastFilterKey.current !== filterKey;
    lastFilterKey.current = filterKey;
    const setBusy = isFilterChange ? setLoading : setSearchLoading;

    const params = windowParams(range);
    // A query parameter, so changing it refetches. Without it the response
    // carries tool counts and tokens only — the same shape the tenant sees.
    if (detail) params.include_llm_detail = true;
    if (debouncedSearch) params.search = debouncedSearch;

    let cancelled = false;
    const load = async () => {
      try {
        setBusy(true);
        const result = await UsageService.getTenantUsage(tenantId, params);
        if (!cancelled) setData(result);
      } catch (error) {
        if (!cancelled) toast.error(apiErrorMessageFormat(error, "Failed to load tenant usage"));
      } finally {
        // Both, not just the one raised. Only one is ever true, and clearing
        // both keeps the hook self-healing when an effect re-runs before its
        // first pass settled — StrictMode does exactly that on mount.
        if (!cancelled) {
          setLoading(false);
          setSearchLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // `filterKey` stands in for the range and the switch: it changes exactly
    // when they do, so the effect always reads fresh values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, filterKey, debouncedSearch]);

  return { data, loading, searchLoading };
};

export default useTenantUsage;
